import device from 'common/support/device';
import debounce from 'lodash/debounce';
import applicationActions from 'common/support/applicationActions';

const focusableClassName = '_fcsc';
const focusIdAttribute = 'data-fcsid';

type FocusHandler = {
  onAction: (action: string) => boolean | string;
  onFocusSet: (isFocused: boolean) => void;
};

type FocusChangedListener = (newId: string | number | null, prevId: string | number | null) => void;

class FocusManager {
  handlers: Record<string | number, FocusHandler> = {};

  focusChangedListeners: FocusChangedListener[] = [];

  currentFocusId: string | number | null = null;

  idCounter = 1;

  constructor() {
    if (document) {
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener(
        'wheel',
        debounce(this.onWheel, 50, { leading: true }),
      );
    }
  }

  getNewUniqueId = () => {
    this.idCounter += 1;
    return this.idCounter;
  };

  onWheel = (e: WheelEvent) => {
    const { deltaY } = e;
    let handled = null;
    const initialFocusId =
      this.currentFocusId ||
      (document &&
        document.querySelector('._fcsc')?.getAttribute(focusIdAttribute));

    if (deltaY < 0) {
      handled = this.callHandlers(initialFocusId, applicationActions.moveUp);
    } else if (deltaY > 0) {
      handled = this.callHandlers(initialFocusId, applicationActions.moveDown);
    }

    if (handled === 'preventDefault' || handled === true) {
      e.preventDefault();
    }
  };

  onKeyDown = (ev: KeyboardEvent) => {
    const action = device.getKeyCodeAction(ev.keyCode);

    // If we ever lose focus, we would want the root to handle
    // the action.
    const initialFocusId =
      this.currentFocusId ||
      (document &&
        document.querySelector('._fcsc')?.getAttribute(focusIdAttribute));

    const handled = this.callHandlers(initialFocusId, action);
    if (
      handled === 'preventDefault' ||
      handled === true ||
      action === 'select'
    ) {
      ev.preventDefault();
    }
  };

  // Actions here can return 4 potential values:
  // true: bool - The component is handling the action fully, so we're not propagating it
  // up nor are we letting the browser handle it. Akin to calling both stopPropagation()
  // and preventDefault()
  //
  // false: bool - The component is not handling anything, so we're propagating the action up
  // the tree. If everything returns false, the browser will handle the event. Akin to
  // calling neither stopPropagation() nor preventDefault()
  //
  // preventDefault: string - The component is allowing other components up the tree to handle
  // the action, but ultimately doesn't want to allow the browser to handle it. Any component
  // further up the tree can hijack this result and change it. (Haven't found a usecase for this
  // just yet). Akin to calling preventDefault() but not stopPropagation()
  //
  // stopPropagation: string - The component doesn't want any other components to handle the
  // action, but wants to allow for the browser to handle the event. Used on the text input
  // to allow the browser to input characters into the field. Akin to calling stopPropagation()
  // but not preventDefault()
  callHandlers = (id: string | number | null | undefined, action: string): boolean | string => {
    const handler = this.handlers[id as keyof typeof this.handlers] && this.handlers[id as keyof typeof this.handlers].onAction;
    let handled: boolean | string = false;
    if (handler) {
      handled = handler(action);
      if (handled && handled !== 'preventDefault') return handled;
    }

    // Now calling the owner's handler since the action was not handled
    const elem =
      document && document.querySelector(`[${focusIdAttribute}='${id}']`);
    const focusOwnerId = this.getFocusOwnerId(elem);
    if (focusOwnerId) {
      const result = this.callHandlers(focusOwnerId, action);
      if (handled === 'preventDefault' && !result) return 'preventDefault';
      return result;
    }

    return false;
  };

  getFocusOwnerId = (elem: Element | ParentNode | null): string | null => {
    if (!elem || !elem.parentNode) return null;
    const { parentNode } = elem;
    if (
      parentNode instanceof Element &&
      parentNode.classList.contains(focusableClassName) &&
      parentNode.hasAttribute(focusIdAttribute)
    ) {
      return parentNode.getAttribute(focusIdAttribute);
    }

    return this.getFocusOwnerId(parentNode);
  };

  getFocusableChildren = (id: string | number | null, elem?: Element | null) => {
    if (!id || (!elem && !document)) return null;

    const root = elem || document;
    const idString = id.toString();

    return Array.from(root.querySelectorAll(`.${focusableClassName}`)).filter(
      child => this.getFocusOwnerId(child) === idString,
    );
  };

  register = (id?: string | number | null, onAction?: FocusHandler['onAction'], onFocusSet?: FocusHandler['onFocusSet']) => {
    this.handlers[id as string | number] = {
      onAction: onAction as FocusHandler['onAction'],
      onFocusSet: onFocusSet as FocusHandler['onFocusSet'],
    };
  };

  unregister = (id?: string | number | null) => {
    delete this.handlers[id as string | number];
  };

  addFocusChangedListener = (onFocusChanged: FocusChangedListener) => {
    this.focusChangedListeners.push(onFocusChanged);
  };

  removeFocusChangedListener = (onFocusChanged: FocusChangedListener) => {
    this.focusChangedListeners = this.focusChangedListeners.filter(
      item => item !== onFocusChanged,
    );
  };

  isFocusAncestor = (id?: string | number | null, elem?: Element | null) => {
    // Returns true if the the focusable item with the id is an ancestor of the current focus
    return !!(elem
      ? elem.querySelector(`[data-fcsid='${this.currentFocusId}']`)
      : document.querySelector(
          `[data-fcsid='${id}'] [data-fcsid='${this.currentFocusId}']`,
        ));
  };

  getElementFocusId = (elem: Element | null) => {
    return elem ? elem.getAttribute(focusIdAttribute) : null;
  };

  callOnFocusChangedHandlers = (newId: string | number | null, prevId: string | number | null) => {
    this.focusChangedListeners.forEach(handler => handler(newId, prevId));
  };

  setCurrentFocus = (id?: string | number | null, ..._args: unknown[]) => {
    const prevFocusHandler = this.handlers[this.currentFocusId as keyof typeof this.handlers]
      ? this.handlers[this.currentFocusId as keyof typeof this.handlers].onFocusSet
      : () => {};
    const nextFocusHandler = this.handlers[id as keyof typeof this.handlers]
      ? this.handlers[id as keyof typeof this.handlers].onFocusSet
      : () => {};
    prevFocusHandler(false);
    const prevId = this.currentFocusId;
    this.currentFocusId = id ?? null;
    nextFocusHandler(true);
    this.callOnFocusChangedHandlers(id ?? null, prevId);
  };

  // Necessary for testing so that we reset the singleton between test cases
  reset = () => {
    this.currentFocusId = null;
    this.handlers = {};
    this.focusChangedListeners = [];
    this.idCounter = 1;
  };

  getFocusableElement = (id: string | number | null | undefined, optionalRoot?: Document | Element | null | undefined) => {
    const base = optionalRoot || document;
    return base.querySelector(`[data-fcsid='${id}']`);
  };
}

export default new FocusManager();
